import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { sessionKeys } from './sessionKeys';

export interface SetVerifiedStatusInput {
  sessionId: string;
  /** A backend SessionVerifiedStatus name. `NotApplicable` clears the decision. */
  verifiedStatus: string;
  /** Optional reason, kept with the decision for the next reviewer. */
  note?: string | null;
  /**
   * Which step positions the applicant must redo. Only read by the API for
   * `ResubmitRequested`; omitting it means "every step that failed".
   */
  resubmitStepPositions?: number[];
}

/**
 * Records the tenant admin's verdict on a run — the second status axis.
 *
 * This does not touch the run's Original Status: that column is our system's own
 * conclusion and stays as it was, which is why both are shown side by side.
 *
 * Invalidates the session detail as well as the lists, because the same verdict is
 * displayed on the detail screen and would otherwise sit there stale until refetch.
 */
export function useSetVerifiedStatus() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: ({
      sessionId,
      verifiedStatus,
      note,
      resubmitStepPositions,
    }: SetVerifiedStatusInput) =>
      apiClient
        .put(`api/v1/t/${tenantId}/sessions/${sessionId}/verified-status`, {
          json: {
            verifiedStatus,
            note: note ?? null,
            resubmitStepPositions: resubmitStepPositions ?? null,
          },
        })
        .then(() => undefined),
    onSuccess: (_data, { sessionId }) => {
      qc.invalidateQueries({ queryKey: sessionKeys.lists() });
      qc.invalidateQueries({ queryKey: sessionKeys.detail(sessionId) });
    },
  });
}
